"use strict";
const isObject = require('lodash/isObject');
const keyBy = require('lodash/keyBy');
const { uuidValidateV4 } = require('../utils');
const { isValidEmail } = require('../email');
const { isGeneralCollection } = require('../collection/utils');
const OWNER = 'owner';
const TID_ATTRIBUTE = '___tid';
const attributeSearchType = ({ type, business_type: businessType }) => {
    switch (businessType) {
        case 'lifecycle': return 'lifecycle_attribute';
        case 'list': return 'list_attribute';
        case 'multiValueFixed': return 'multivalue_attribute';
        case OWNER: return OWNER;
        default: break;
    }
    switch (type.toLowerCase()) {
        case 'date':
            return 'date_attribute';
        case 'text':
            return 'string_attribute';
        case 'numeric':
        case 'currency':
            return 'number_attribute';
        default: break;
    }
    return undefined;
};
const mapAccountsByName = (accounts) => keyBy(accounts, 'name');
const getUUIDSFromAccountResponse = (accounts) => accounts.reduce((uuids, account) => {
    const { selected_fields: selectedFields = [] } = account;
    return selectedFields.reduce((res, selectedField) => {
        const isTid = selectedField && uuidValidateV4(selectedField.owner_tid_value);
        return isTid ? [...res, selectedField.owner_tid_value] : res;
    }, uuids);
}, []);
const replaceRoleTidFieldsIfNeeded = (fields) => (fields.map((field) => {
    const { attribute: fieldAttribute, _extra = {}, index } = field;
    const { field_nam: fieldName } = _extra;
    const attribute = fieldAttribute || fieldName || '';
    return attribute.includes(TID_ATTRIBUTE) ? {
        type: OWNER,
        account_role: attribute.replace(TID_ATTRIBUTE, ''),
        field_display_name: attribute,
        _extra: { ..._extra, field_name: attribute, owner_field: 'email' },
        index,
    } : field;
}));
const removeExtra = ({ index, _extra, ...filed }) => filed;
const extractSelectedFieldsByKey = (accounts, accountFieldsArray, uuidMap) => {
    /* eslint-disable no-param-reassign */
    accountFieldsArray.fields.forEach((field, index) => {
        accounts.forEach((account) => {
            if (account.selected_fields) {
                const selectedField = account.selected_fields[index];
                if (field.attribute !== 'name' && field.attribute && selectedField !== null && selectedField !== undefined) {
                    account[field.attribute] = selectedField;
                }
                if (field.type === OWNER && isObject(selectedField)) {
                    const { owner_tid_value: tidValue, owner_user_email: email } = selectedField;
                    const isEmailField = field.field_display_name.includes(TID_ATTRIBUTE);
                    const uidFieldName = isEmailField ? 'email' : 'fullName';
                    account[field.field_display_name] = email || (isValidEmail(tidValue) ? tidValue : (uuidMap[tidValue] || {})[uidFieldName]);
                }
            }
        });
    });
};
const buildUserQueryByAccountIdAndUserId = (userId, accountId, fields) => ({
    terms: [
        {
            type: 'string',
            term: 'identifier',
            in_list: [
                userId,
            ],
        },
        {
            type: 'parent_account',
            terms: [
                {
                    type: 'string',
                    term: 'identifier',
                    in_list: [
                        accountId,
                    ],
                },
            ],
        },
    ],
    fields,
});
const getFieldsWithUUIDValues = (selectedFields, uuidMap) => {
    if (!selectedFields) {
        return selectedFields;
    }
    return selectedFields.map((selectedField) => {
        if (!selectedField?.owner_tid_value) {
            return selectedField;
        }
        const ownerTidValue = uuidMap?.[selectedField.owner_tid_value]?.email || selectedField.owner_tid_value;
        const ownerValue = uuidMap?.[selectedField.owner_tid_value]?.fullName || selectedField.owner_value;
        return { ...selectedField, owner_tid_value: ownerTidValue, owner_value: ownerValue };
    });
};
/**
 * Merges search terms with account terms based on the segment type.
 *
 * @param {Array} terms - The original search terms.
 * @param {Array} accountTerms - The account search terms.
 * @param {string} segmentType - The type of segment, which can be 'account' or other types.
 * @returns {Array} The merged search terms.
 */
const mergeTermsWithAccountTerms = (terms, accountTerms, segmentType) => {
    if (!accountTerms?.length) {
        return terms;
    }
    if (segmentType?.toLowerCase() === 'account') {
        return [
            ...terms ?? [],
            ...accountTerms,
        ];
    }
    const updatedTerms = JSON.parse(JSON.stringify(terms));
    const parentAccountTerm = updatedTerms?.find((term) => term.type === 'parent_account');
    if (parentAccountTerm) {
        parentAccountTerm.terms = [
            ...parentAccountTerm.terms ?? [],
            ...accountTerms,
        ];
        return updatedTerms;
    }
    return [
        ...terms ?? [],
        {
            type: 'parent_account',
            terms: accountTerms,
        },
    ];
};
/**
     * Getting path for request depends on segment type
     * @return {string}
     */
const getSearchTypeUrlPart = (searchType, collectionType = null) => {
    const lowerSearchType = searchType.toLowerCase();
    switch (lowerSearchType) {
        case 'account':
            return 'accounts';
        case 'user':
        case 'internal_user':
            return 'users';
        case 'collection':
            return isGeneralCollection(collectionType) ? 'general/collections' : 'accounts/collections';
        default:
            return 'accounts';
    }
};
module.exports = {
    attributeSearchType,
    mapAccountsByName,
    getUUIDSFromAccountResponse,
    replaceRoleTidFieldsIfNeeded,
    removeExtra,
    extractSelectedFieldsByKey,
    buildUserQueryByAccountIdAndUserId,
    getFieldsWithUUIDValues,
    mergeTermsWithAccountTerms,
    getSearchTypeUrlPart,
};
//# sourceMappingURL=utils.js.map